import React, { useState, useEffect } from 'react';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../../utils/errorLogging';
import { formatCurrency, parseCurrency } from '../../../utils/formatters';
import modalStyles from '../../../styles/modals.module.css';

const PaymentFormModal = ({ open, onClose, onSubmit, onSubmitRef }) => {
  const { logError } = useErrorLogging();
  const [isLoading, setIsLoading] = useState(false);
  const [isAmountFocused, setIsAmountFocused] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    due_date: '',
    status: 'pending',
    notes: '',
  });
  const [errors, setErrors] = useState({});

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        amount: '',
        due_date: '',
        status: 'pending',
        notes: '',
      });
      setErrors({});
    }
  }, [open]);

  const validateForm = () => {
    const newErrors = {};

    if (
      !formData.amount ||
      isNaN(parseFloat(formData.amount)) ||
      parseFloat(formData.amount) <= 0
    ) {
      newErrors.amount = 'נדרש סכום חיובי';
    }

    if (!formData.due_date) {
      newErrors.due_date = 'נדרש תאריך';
    }

    if (!formData.status) {
      newErrors.status = 'נדרש סטטוס';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = e => {
    const { name, value } = e.target;

    // Special handling for amount field
    if (name === 'amount') {
      // Only update if it's a valid number or empty
      const numericValue = parseCurrency(value);
      if (!isNaN(numericValue) || value === '') {
        setFormData(prev => ({
          ...prev,
          [name]: value === '' ? '' : numericValue,
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async e => {
    if (e) e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const paymentData = {
        amount: parseFloat(formData.amount),
        due_date: formData.due_date,
        status: formData.status,
        notes: formData.notes || null,
      };

      await onSubmit(paymentData);
      onClose();
    } catch (error) {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_MUTATION,
        message: 'Failed to save payment',
        metadata: { formData },
      });
      setErrors(prev => ({
        ...prev,
        submit: 'שגיאה בשמירת התשלום. אנא נסה שוב.',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Register submit handler with ModalManager
  useEffect(() => {
    if (open && onSubmitRef) {
      onSubmitRef(handleSubmit);
    }
  }, [open, onSubmitRef, handleSubmit]);

  if (!open) return null;

  return (
    <div className={modalStyles.modal} dir="rtl">
      <div className={modalStyles.header}>
        <h2 className={modalStyles.headerTitle}>הוסף תשלום</h2>
      </div>

      <div className={modalStyles.content}>
        {errors.submit && <div className={modalStyles.error}>{errors.submit}</div>}

        <div className={modalStyles.formGrid}>
          <div className={modalStyles.formRow}>
            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                <span className={modalStyles.fieldRequired}>*</span>
                סכום
              </label>
              <input
                type="text"
                className={modalStyles.fieldInput}
                name="amount"
                value={
                  isAmountFocused
                    ? formData.amount || ''
                    : formData.amount === ''
                      ? ''
                      : formatCurrency(formData.amount)
                }
                onChange={handleChange}
                onFocus={() => setIsAmountFocused(true)}
                onBlur={() => setIsAmountFocused(false)}
                disabled={isLoading}
                placeholder="₪0.00"
              />
              {errors.amount && <div className={modalStyles.fieldError}>{errors.amount}</div>}
            </div>

            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                <span className={modalStyles.fieldRequired}>*</span>
                תאריך
              </label>
              <input
                type="date"
                className={modalStyles.fieldInput}
                name="due_date"
                value={formData.due_date}
                onChange={handleChange}
                disabled={isLoading}
              />
              {errors.due_date && <div className={modalStyles.fieldError}>{errors.due_date}</div>}
            </div>
          </div>

          <div className={modalStyles.field}>
            <label className={modalStyles.fieldLabel}>
              <span className={modalStyles.fieldRequired}>*</span>
              סטטוס
            </label>
            <select
              className={modalStyles.fieldSelect}
              name="status"
              value={formData.status}
              onChange={handleChange}
              disabled={isLoading}
            >
              <option value="pending">ממתין</option>
              <option value="completed">שולם</option>
              <option value="cancelled">בוטל</option>
            </select>
            {errors.status && <div className={modalStyles.fieldError}>{errors.status}</div>}
          </div>

          <div className={modalStyles.field}>
            <label className={modalStyles.fieldLabel}>
              הערות
              <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
            </label>
            <input
              type="text"
              className={modalStyles.fieldInput}
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              disabled={isLoading}
              placeholder="הוסף הערה..."
            />
          </div>
        </div>
      </div>

      <div className={modalStyles.footer}>
        <button onClick={onClose} className={modalStyles.cancelButton} disabled={isLoading}>
          ביטול
        </button>
        <button onClick={handleSubmit} className={modalStyles.primaryButton} disabled={isLoading}>
          {isLoading ? 'שומר...' : 'שמור'}
        </button>
      </div>
    </div>
  );
};

export default PaymentFormModal;
